import { supabase } from './supabase';
import { APP_URL, SUPPORT_EMAIL, CRM_EMAIL, OWNER_EMAIL } from './org';
import { emailTemplate, ahfcTemplate } from './emailTemplate';

const CRM_URL = APP_URL;
// Queue-based send — goes to the CRM inbox via notification_queue
const sendEmail = async (subject, html) => {
  try {
    const { error } = await supabase.from('notification_queue').insert({
      to_emails: [CRM_EMAIL],
      subject,
      html,
    });
    if (error) console.error('Notification queue error:', error);
  } catch (err) {
    console.error('Notification queue failed:', err);
  }
};

// Direct send — bypasses central routing; used only for sensitive personal emails
// (welcome with temp password, password reset links) and candidate-facing
// onboarding emails. `bcc` is opt-in per call (only onboarding-process
// notifiers pass it) — not every direct send should copy the office.
const sendDirect = async (to, subject, html, bcc, cc) => {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: { to, subject, html, bypass_central: true, ...(bcc ? { bcc } : {}), ...(cc ? { cc } : {}) },
    });
    if (error) { console.error('Direct email error:', error); return { error }; }
    return { error: null };
  } catch (err) {
    console.error('Direct email failed:', err);
    return { error: err };
  }
};

// bcc target for the onboarding-process email notifiers below (application
// received/rejected, offer/orientation ready-to-sign, cert requests, Gusto/
// AxisCare nudge, training assigned/reminder) — office wants a copy of every
// candidate-facing onboarding email. Not applied to notifyApplicantInvite
// (pre-application recruiting outreach) or the non-candidate notifiers above
// (task assignments, CAERA account welcome, password resets).
const ONBOARDING_BCC = OWNER_EMAIL;

// ── Info box helper ───────────────────────────────────────────────────────────
const infoBox = (items, color = '#0A1628', bg = '#EAF0F8', border = '#B9CBE0') => `
  <div style="background:${bg};border-left:4px solid ${border};border-radius:0 8px 8px 0;padding:16px 20px;margin:16px 0;">
    ${items.map(([label, value]) => value ? `
      <div style="display:flex;gap:8px;margin-bottom:6px;font-size:13px;">
        <span style="color:${color};font-weight:500;min-width:120px;flex-shrink:0;">${label}:</span>
        <span style="color:#45526B;">${value}</span>
      </div>` : '').join('')}
  </div>`;

// ── 1. Task assigned ──────────────────────────────────────────────────────────
export const notifyTaskAssigned = async (task, assigneeName, assigneeEmail, assignedByName) => {
  const subject = `Task assigned to ${assigneeName} — ${task.title}`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      <strong style="color:#0A1628;">${assignedByName}</strong> assigned a new task to <strong style="color:#0A1628;">${assigneeName}</strong>.
    </p>
    ${infoBox([
      ['Assigned To', assigneeName],
      ['Task',        task.title],
      ['Priority',    task.priority || 'Normal'],
      ['Client',      task.client_name],
      ['Due Date',    task.due_date],
      ['Notes',       task.notes],
    ])}`;
  await sendEmail(subject, emailTemplate('New Task Assigned', body, 'View Tasks', `${CRM_URL}/tasks`, '#1A3A6B', 'Task'));
};

// The office's "new intake" alert is a Chat line from the notify_office
// trigger (supabase/functions/_shared/office-notices.ts) since 2026-10-01.

// ── 3. Welcome new staff — sent directly to individual (contains temp password) ──
export const notifyWelcomeNewStaff = async (staffEmail, staffName, tempPassword) => {
  if (!staffEmail) return;
  const subject = `Welcome to CAERA — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${staffName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      Your CAERA account has been created. Here are your login details:
    </p>
    ${infoBox([
      ['Login URL',      `<a href="${CRM_URL}/login" style="color:#1A3A6B;">${CRM_URL}/login</a>`],
      ['Email',          staffEmail],
      ['Temp Password',  `<code style="background:#E8E0D0;padding:2px 8px;border-radius:4px;font-family:monospace;">${tempPassword}</code>`],
    ])}
    <div style="background:#FFF3DC;border-left:4px solid #F0C870;border-radius:0 8px 8px 0;padding:12px 16px;margin:16px 0;font-size:13px;color:#8A6020;">
      ⚠ Please change your password immediately after your first login.
    </div>`;
  await sendDirect(staffEmail, subject, emailTemplate('Welcome to CAERA', body, 'Login Now', `${CRM_URL}/login`, '#1A3A6B', 'Account'));
};

// ── 6. Password reset — sent directly to individual (contains reset link) ─────
export const notifyPasswordReset = async (staffEmail, staffName, resetLink) => {
  if (!staffEmail) return;
  const subject = `Password reset — CAERA`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${staffName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      We received a request to reset your CAERA password. Click the button below — this link expires in <strong>24 hours</strong>.
    </p>
    <p style="color:#6B7A94;font-size:13px;margin-top:16px;">
      If you didn't request this, you can safely ignore this email.
    </p>`;
  await sendDirect(staffEmail, subject, emailTemplate('Password Reset Request', body, 'Reset My Password', resetLink, '#C83030', 'Security'));
};

// ── 7. Offer letter ready to sign — sent to a candidate (contains sign link) ───
// Office invites a prospective candidate to apply — points at the public
// /careers/apply form (no token, unlike the offer/onboarding sign links,
// since the application form itself isn't gated per-invite).
// 2026-08-08: brought into the ONBOARDING_BCC set — was originally scoped as
// "pre-application recruiting outreach" and left out, but the office wants a
// copy of every candidate-facing HR email, invite included.
export const notifyApplicantInvite = async (candidateEmail, candidateName, applyLink, lang = 'en') => {
  if (!candidateEmail) return;
  if (lang === 'es') {
    const subject = `Invitación para Aplicar — A Heart For Care`;
    const greeting = candidateName ? `Estimado(a) ${candidateName}:` : 'Estimado(a) aplicante:';
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">${greeting}</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Nos comunicamos con usted para invitarlo(a) a aplicar para un puesto en A Heart For Care. Creemos que su experiencia podría ser una buena opción para una de nuestras vacantes actuales. Por favor complete nuestro formulario de solicitud utilizando el enlace a continuación a la brevedad posible.
      </p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 6px;"><strong>Tenga listos estos documentos</strong> — la solicitud los pide todos, y así podremos enviarle una oferta de inmediato:</p>
      <ul style="color:#45526B;font-size:14px;line-height:1.8;margin:0 0 16px;padding-left:20px;">
        <li>Currículum</li>
        <li>Licencia de conducir o identificación oficial</li>
        <li>Tarjeta vigente de RCP / Primeros Auxilios</li>
        <li>Resultado de prueba de TB (últimos 12 meses)</li>
        <li>Licencia o certificado de enfermería (RN, LPN, CNA)</li>
        <li>Su Número de Seguro Social (para la verificación de antecedentes; se guarda cifrado)</li>
      </ul>`;
    await sendDirect(candidateEmail, subject, ahfcTemplate('Invitación para Aplicar', body, 'Aplicar Ahora', applyLink, '#7C3AED', 'Empleo'), ONBOARDING_BCC);
    return;
  }
  const subject = `Invitation to Apply — A Heart For Care`;
  const greeting = candidateName ? `Dear ${candidateName},` : 'Dear Applicant,';
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">${greeting}</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      We are reaching out to invite you to apply for a position with A Heart For Care. We believe your background may be a strong match for one of our current openings. Please complete our application form using the link below at your earliest convenience.
    </p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 6px;"><strong>Please have these ready</strong> — the application asks for all of them, so we can send you an offer straight away:</p>
    <ul style="color:#45526B;font-size:14px;line-height:1.8;margin:0 0 16px;padding-left:20px;">
      <li>Resume</li>
      <li>Driver's license or government ID</li>
      <li>Current CPR / First Aid card</li>
      <li>TB test result (within the last 12 months)</li>
      <li>Nursing license or certificate (RN, LPN, CNA)</li>
      <li>Your Social Security Number (for the background check; stored encrypted)</li>
    </ul>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Invitation to Apply', body, 'Apply Now', applyLink, '#7C3AED', 'Careers'), ONBOARDING_BCC);
};

// Missing application documents (2026-09-07) — every applicant is asked for
// resume, ID, CPR card, TB result and (RN/LPN/CNA) license up front, and an
// offer cannot go out until they are all on file. Older applications and
// hand-added candidates may be short; this asks for the gaps by reply.
export const notifyApplicationDocumentsMissing = async (candidateEmail, candidateName, missingLabels, lang = 'en') => {
  if (!candidateEmail || !missingLabels?.length) return;
  const list = `<ul style="color:#45526B;font-size:14px;line-height:1.8;margin:0 0 16px;padding-left:20px;">${missingLabels.map(l => `<li>${l}</li>`).join('')}</ul>`;
  if (lang === 'es') {
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Estamos listos para enviarle una oferta, pero primero necesitamos los siguientes documentos:</p>
      ${list}
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Responda a este correo con fotos o PDF claros de cada uno, o envíelos por mensaje de texto al 470-470-6640. En cuanto los recibamos, le enviaremos su oferta para firmar.</p>`;
    await sendDirect(candidateEmail, `Documentos pendientes antes de su oferta — A Heart For Care`, ahfcTemplate('Documentos Pendientes', body, null, null, '#D97706', 'Empleo'), ONBOARDING_BCC);
    return;
  }
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">We're ready to send you an offer, but we need the following documents first:</p>
    ${list}
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Please reply to this email with a clear photo or PDF of each, or text them to 470-470-6640. As soon as we have them, your offer will be on its way to sign.</p>`;
  await sendDirect(candidateEmail, `Documents needed before your offer — A Heart For Care`, ahfcTemplate('Documents Needed', body, null, null, '#D97706', 'Careers'), ONBOARDING_BCC);
};

// Agency/contracted workers (Policy A-2.4) never get an offer letter — no
// employment relationship with AHFC — but still sign the Background Check
// Consent, so notifyOfferLetterReady's "your offer letter" copy would be
// wrong here.
export const notifyConsentReady = async (candidateEmail, candidateName, signLink, lang = 'en') => {
  if (!candidateEmail) return;
  if (lang === 'es') {
    const subject = `Documento listo para firmar — A Heart For Care`;
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Su Consentimiento de Verificación de Antecedentes de A Heart For Care ya está listo para revisar y firmar.
      </p>`;
    await sendDirect(candidateEmail, subject, ahfcTemplate('Documento Listo Para Firmar', body, 'Revisar y Firmar', signLink, '#0DA06A', 'Onboarding'), ONBOARDING_BCC);
    return;
  }
  const subject = `A document is ready for your signature — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      Your Background Check Consent from A Heart For Care is ready to review and sign.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Document Ready to Sign', body, 'Review & Sign', signLink, '#0DA06A', 'Onboarding'), ONBOARDING_BCC);
};

// Generic version of notifyConsentReady/notifyOfferLetterReady for the
// retroactive "Send request" actions in PersonnelProfile.js's Personnel File
// checklist (src/lib/staffDocuments.js) — any staff_documents doc_type, not
// just the fixed set the onboarding pipeline sends. Parameterized on a
// human doc label instead of one bespoke function per document type.
export const notifyDocumentRequestReady = async (staffEmail, staffName, docLabel, signLink, lang = 'en') => {
  if (!staffEmail) return;
  if (lang === 'es') {
    const subject = `Documento listo para firmar — A Heart For Care`;
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${staffName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Un documento (${docLabel}) de A Heart For Care está listo para revisar y firmar.
      </p>`;
    await sendDirect(staffEmail, subject, ahfcTemplate('Documento Listo Para Firmar', body, 'Revisar y Firmar', signLink, '#0DA06A', 'Personnel File'), ONBOARDING_BCC);
    return;
  }
  const subject = `A document is ready for your signature — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${staffName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      A document (${docLabel}) from A Heart For Care is ready to review and sign.
    </p>`;
  await sendDirect(staffEmail, subject, ahfcTemplate('Document Ready to Sign', body, 'Review & Sign', signLink, '#0DA06A', 'Personnel File'), ONBOARDING_BCC);
};

export const notifyOfferLetterReady = async (candidateEmail, candidateName, signLink) => {
  if (!candidateEmail) return;
  const subject = `Your offer letter from A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      Congratulations! Your offer letter from A Heart For Care is ready to review and sign.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Your Offer Letter', body, 'Review & Sign Offer', signLink, '#0DA06A', 'Onboarding'), ONBOARDING_BCC);
};

// Manual reminder for a still-unsigned offer bundle (offer letter + job
// description + background check consent, sent together as one action) —
// same sign link, reminder-toned copy so it doesn't read as a fresh offer.
// Triggered by a "Send Reminder" button in the Offer Sent lane.
export const notifyOfferLetterReminder = async (candidateEmail, candidateName, signLink) => {
  if (!candidateEmail) return;
  const subject = `Reminder: your offer letter is waiting for your signature — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      This is a friendly reminder that your offer letter from A Heart For Care is still waiting for your review and signature.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Offer Letter Reminder', body, 'Review & Sign Offer', signLink, '#D97706', 'Reminder'), ONBOARDING_BCC);
};

// ── 7b. Placement Acknowledgment reminder — agency-placed nurse ──────────────
// She is not an AHFC employee and there is no offer letter: the document is the
// Placement Acknowledgment & GAPP Policy Attestation for her assignment through
// her agency. The office route hands the link to the agency by design; this
// reminder is the one AHFC email in that flow, so it says exactly what it is.
export const notifyPlacementAcknowledgmentReminder = async (candidateEmail, candidateName, agencyName, signLink) => {
  if (!candidateEmail) return;
  const subject = `Reminder: your Placement Acknowledgment is waiting for your signature — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      A friendly reminder: the <strong>Placement Acknowledgment &amp; GAPP Policy Attestation</strong> for your assignment with A Heart For Care through ${agencyName || 'your agency'} is still waiting for your signature. It is a one-page acknowledgment, not an employment document — ${agencyName || 'your agency'} remains your employer.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Placement Acknowledgment Reminder', body, 'Review & Sign', signLink, '#D97706', 'Reminder'), ONBOARDING_BCC);
};

// ── 8. Orientation & Training checklist ready to sign — sent to a candidate ──
// (contains sign link). Works the same whether orientation happened in
// person, virtually, or asynchronously — this just delivers the checklist
// the office already filled in for a remote signature (see OnboardingPipeline.js).
export const notifyOrientationReady = async (candidateEmail, candidateName, signLink, lang = 'en') => {
  if (!candidateEmail) return;
  if (lang === 'es') {
    const subject = `Su lista de Orientación y Capacitación está lista para firmar — A Heart For Care`;
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Su lista de verificación de Orientación y Capacitación ya está lista para su firma. Revísela y firme usando el enlace a continuación.
      </p>`;
    await sendDirect(candidateEmail, subject, ahfcTemplate('Orientación y Capacitación', body, 'Revisar y Firmar', signLink, '#1A3A6B', 'Onboarding'), ONBOARDING_BCC);
    return;
  }
  const subject = `Your Orientation & Training checklist is ready to sign — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      Your Orientation &amp; Training checklist is ready for your signature. Please review and sign using the link below.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Orientation & Training', body, 'Review & Sign', signLink, '#1A3A6B', 'Onboarding'), ONBOARDING_BCC);
};

// Manual reminder for a still-unsigned Orientation & Training checklist —
// same sign link, reminder-toned copy. Triggered by "Resend link" in the
// Orientation & Training lane.
export const notifyOrientationReminder = async (candidateEmail, candidateName, signLink, lang = 'en') => {
  if (!candidateEmail) return;
  if (lang === 'es') {
    const subject = `Recordatorio: su lista de Orientación y Capacitación sigue pendiente de firma — A Heart For Care`;
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
        Este es un recordatorio amistoso de que su lista de verificación de Orientación y Capacitación sigue pendiente de su firma. Revísela y firme usando el enlace a continuación.
      </p>`;
    await sendDirect(candidateEmail, subject, ahfcTemplate('Recordatorio de Orientación', body, 'Revisar y Firmar', signLink, '#D97706', 'Recordatorio'), ONBOARDING_BCC);
    return;
  }
  const subject = `Reminder: your Orientation & Training checklist is still pending signature — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      This is a friendly reminder that your Orientation &amp; Training checklist is still waiting for your signature. Please review and sign using the link below.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Orientation Reminder', body, 'Review & Sign', signLink, '#D97706', 'Reminder'), ONBOARDING_BCC);
};

// ── 9. Training module assigned — no-login link, sent to a caregiver (or their office email) ─
// moduleTitles: array of one or more newly-assigned module titles. One email/link
// covers everything currently pending for this person — the link page walks through
// all of it with a progress bar, so a multi-module assignment never sends multiple links.
export const notifyTrainingAssigned = async (staffEmail, staffName, moduleTitles, link) => {
  if (!staffEmail) return;
  const titles = Array.isArray(moduleTitles) ? moduleTitles : [moduleTitles];
  const subject = titles.length === 1
    ? `Training assigned: ${titles[0]} — A Heart For Care`
    : `${titles.length} training modules assigned — A Heart For Care`;
  const list = titles.map(t => `<li style="color:#45526B;font-size:14px;line-height:1.7;">${t}</li>`).join('');
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${staffName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      You've been assigned ${titles.length === 1 ? 'a new training module' : `${titles.length} new training modules`}. No login needed — tap below to complete ${titles.length === 1 ? 'it' : 'them'} on your phone.
    </p>
    <ul style="margin:0 0 16px;padding-left:20px;">${list}</ul>`;
  await sendDirect(staffEmail, subject, ahfcTemplate('Training Assigned', body, 'Start Training', link, '#7C3AED', 'Training'), ONBOARDING_BCC);
};

// Manual reminder for a still-incomplete course — same no-login link as the
// original assignment, reminder-toned copy so it doesn't read as a fresh
// assignment. Triggered by a "Send Reminder" button, not automatically —
// the daily digest already surfaces overdue training to the office; this is
// the office choosing to nudge the staff member directly.
export const notifyTrainingReminder = async (staffEmail, staffName, courseTitle, link) => {
  if (!staffEmail) return;
  const subject = `Reminder: ${courseTitle} still pending — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${staffName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">
      This is a friendly reminder that your <strong>${courseTitle}</strong> training is still incomplete. No login needed — tap below to pick up where you left off.
    </p>`;
  await sendDirect(staffEmail, subject, ahfcTemplate('Training Reminder', body, 'Continue Training', link, '#D97706', 'Training'), ONBOARDING_BCC);
};

// ── Clinical onboarding requirement requested (CPR/First Aid, TB Screening) ──
// No self-service upload exists for these (no candidate login) — the email
// just asks them to arrange it and let the office know; office marks it on
// file manually once they have the actual result/card in hand.
export const notifyCertRequested = async (candidateEmail, candidateName, certLabel, lang = 'en') => {
  if (!candidateEmail) return;
  const subject = lang === 'es' ? `Acción requerida: ${certLabel} — A Heart For Care` : `Action needed: ${certLabel} — A Heart For Care`;
  const body = lang === 'es' ? `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      Como parte de su incorporación, por favor complete su <strong>${certLabel}</strong> y avísenos en cuanto esté
      listo — responda a este correo o contacte a nuestra oficina para que podamos registrarlo.
    </p>` : `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      As part of your onboarding, please complete your <strong>${certLabel}</strong> and let us know once it's done —
      reply to this email or contact our office so we can get it on file for you.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate(lang === 'es' ? 'Requisito de Incorporación' : 'Onboarding Requirement', body, null, null, '#D97706', 'Onboarding'), ONBOARDING_BCC);
};

// Manual reminder for a still-outstanding clinical requirement (CPR/First
// Aid, TB Screening) — same ask, reminder-toned copy. Triggered by a
// "Remind" button next to the requirement once it's been requested.
export const notifyCertReminder = async (candidateEmail, candidateName, certLabel, lang = 'en') => {
  if (!candidateEmail) return;
  const subject = lang === 'es' ? `Recordatorio: ${certLabel} sigue pendiente — A Heart For Care` : `Reminder: ${certLabel} still pending — A Heart For Care`;
  const body = lang === 'es' ? `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      Este es un recordatorio amistoso de que su <strong>${certLabel}</strong> sigue pendiente como parte de su incorporación.
      Avísenos en cuanto esté listo — responda a este correo o contacte a nuestra oficina para que podamos registrarlo.
    </p>` : `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      This is a friendly reminder that your <strong>${certLabel}</strong> is still pending as part of your onboarding.
      Please let us know once it's done — reply to this email or contact our office so we can get it on file for you.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate(lang === 'es' ? 'Recordatorio de Incorporación' : 'Onboarding Reminder', body, null, null, '#D97706', 'Reminder'), ONBOARDING_BCC);
};

// The office's "new application" alert is a Chat line from the notify_office
// trigger (supabase/functions/_shared/office-notices.ts) since 2026-10-01.

// ── Application not moving forward — sent when the office rejects an ────────
// application in the Onboarding Pipeline (rejectApplication). Kept
// deliberately brief and generic — no specific reason given, matching
// standard hiring-communication practice.
export const notifyApplicationRejected = async (applicantEmail, applicantName, lang = 'en') => {
  if (!applicantEmail) return;
  if (lang === 'es') {
    const subject = `Actualización sobre su solicitud — A Heart For Care`;
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Estimado(a) ${applicantName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
        Gracias por su interés en A Heart For Care y por el tiempo dedicado a su solicitud. En este momento, hemos decidido no continuar con su solicitud para el puesto actual. Le deseamos éxito en su búsqueda de empleo y lo(a) invitamos a postularse nuevamente en el futuro si surge una vacante que se ajuste a su perfil.
      </p>`;
    await sendDirect(applicantEmail, subject, ahfcTemplate('Actualización de Solicitud', body, null, null, '#6B7A94', 'Empleo'), ONBOARDING_BCC);
    return;
  }
  const subject = `Update on your application — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Dear ${applicantName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      Thank you for your interest in A Heart For Care and for the time you put into your application. At this time, we've decided not to move forward with your application for the current position. We wish you success in your job search and welcome you to apply again in the future if a role that fits your background opens up.
    </p>`;
  await sendDirect(applicantEmail, subject, ahfcTemplate('Application Update', body, null, null, '#6B7A94', 'Careers'), ONBOARDING_BCC);
};

// ── Systems & Access Setup — manual "still with us" nudge ────────────────────
// Not automatic — a button the office clicks from the Onboarding Pipeline
// when payroll/scheduling setup is taking a few days, so the candidate isn't
// left wondering if anything is happening.
export const notifyGustoAxisCareUpdate = async (candidateEmail, candidateName, lang = 'en', isAgency = false) => {
  if (!candidateEmail) return;
  const systemsEs = isAgency ? 'de programación (AxisCare)' : 'de nómina (Gusto) y de programación (AxisCare)';
  const systemsEn = isAgency ? 'scheduling (AxisCare) account' : 'payroll (Gusto) and scheduling (AxisCare) accounts';
  if (lang === 'es') {
    const subject = `Actualización de su incorporación — A Heart For Care`;
    const body = `
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hola ${candidateName},</p>
      <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
        Queríamos avisarle que seguimos trabajando en la configuración de su cuenta ${systemsEs}. No necesita hacer nada en este momento — nos pondremos en contacto en cuanto todo esté listo y sea el momento de continuar con su Orientación y Capacitación. Gracias por su paciencia.
      </p>`;
    await sendDirect(candidateEmail, subject, ahfcTemplate('Actualización de Incorporación', body, null, null, '#1A3A6B', 'Onboarding'), ONBOARDING_BCC);
    return;
  }
  const subject = `An update on your onboarding — A Heart For Care`;
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">Hi ${candidateName},</p>
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 12px;">
      Just wanted to let you know we're still working on setting up your ${systemsEn}. There's nothing you need to do right now — we'll reach out as soon as everything's ready and it's time to move on to Orientation &amp; Training. Thanks for your patience.
    </p>`;
  await sendDirect(candidateEmail, subject, ahfcTemplate('Onboarding Update', body, null, null, '#1A3A6B', 'Onboarding'), ONBOARDING_BCC);
};

// Authorisation expiry is handled entirely by the daily digest (the "Authorisations
// Expiring" section), so no real-time per-client expiry email is sent from here.

// ── A GAPP submission went to DCH — sent to the family, cc the office ───────
// One email per submission, in the wording for its kind:
//   application     — initial or transfer in: "the application has been submitted"
//   renewal         — the yearly renewal of a child already in service
//   reconsideration — the family's request, uploaded by AHFC, to look again
//                     at the hours approved (or, rarely, a denial)
// A renewal must never read like a first application: the family is told the
// care carries on, and until when the current authorization runs.
// The caller owns the send-once guard (pa_cycles.parent_notified_at, and
// clients.dma80_parent_notified_at for an application) and the "no address on
// file" block, so by the time this runs there is an address to send to.
//
// Family mail, so: no CAERA links, no jargon, no promise of an outcome, and a
// footer that invites a reply instead of forbidding one. "About 30 days" is
// what DCH usually takes, phrased as an expectation rather than a commitment.
const AHFC_TEL = '470-470-6640';

export const notifyGappSubmitted = async (client, { kind = 'application', submittedOn = new Date(), currentExpiry = null, inService = true, to: toAddress = null } = {}) => {
  // The address on file unless the office corrected it for this send.
  const to = (toAddress || client.guardian_email || '').trim();
  if (!to) return { error: new Error('No guardian email on file') };

  const childFirst = client.first_name || 'your child';
  const childFull = [client.first_name, client.last_name].filter(Boolean).join(' ') || childFirst;
  const guardian = [client.guardian_first_name, client.guardian_last_name].filter(Boolean).join(' ');
  const es = /^\s*(spanish|espa)/i.test(client.language || '');
  const decisionBy = new Date(submittedOn.getTime() + 30 * 86400000);
  const fmt = (d, locale) => d.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });

  const contactBox = (heading, callWord, emailWord) => `
    <div style="background:#F8F6F0;border:1px solid #E8E0D0;border-radius:8px;padding:16px 20px;margin:20px 0 0;">
      <div style="color:#0A1628;font-weight:500;font-size:13px;margin-bottom:6px;">${heading}</div>
      <div style="color:#45526B;font-size:13px;line-height:1.9;">
        ${callWord} <a href="tel:+1${AHFC_TEL.replace(/-/g, '')}" style="color:#1A3A6B;text-decoration:none;font-weight:500;">${AHFC_TEL}</a><br />
        ${emailWord} <a href="mailto:${SUPPORT_EMAIL}" style="color:#1A3A6B;text-decoration:none;font-weight:500;">${SUPPORT_EMAIL}</a>
      </div>
    </div>`;

  const facts = (rows) => `
    <div style="background:#EAF0F8;border-left:4px solid #B9CBE0;border-radius:0 8px 8px 0;padding:16px 20px;margin:16px 0;">
      ${rows.map(([label, value], i) => `
      <div style="${i === rows.length - 1 ? '' : 'margin-bottom:6px;'}font-size:13px;"><span style="color:#0A1628;font-weight:500;display:inline-block;min-width:150px;">${label}</span><span style="color:#45526B;">${value}</span></div>`).join('')}
    </div>`;

  const nextSteps = (heading, items) => `
    <div style="background:#EAF0F8;border-left:4px solid #A8CEFA;border-radius:0 8px 8px 0;padding:16px 20px;margin:16px 0;">
      <div style="color:#0A1628;font-weight:500;margin-bottom:8px;font-size:13px;">${heading}</div>
      <ul style="color:#45526B;font-size:13px;line-height:1.8;margin:8px 0;padding-left:20px;">
        ${items.map((i) => `<li>${i}</li>`).join('')}
      </ul>
    </div>`;

  const p = (t) => `<p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">${t}</p>`;

  const application = es ? {
    subject: `La solicitud de GAPP de ${childFirst} ha sido enviada`,
    title: `La solicitud de ${childFirst} ha sido enviada`,
    category: 'Solicitud GAPP',
    footer: 'Este mensaje es para la familia del niño mencionado. Puede responder a este correo o llamarnos con cualquier pregunta.',
    body: `
      ${p(`Estimado/a ${guardian || 'padre, madre o tutor'}:`)}
      ${p(`A Heart For Care ha enviado el expediente de solicitud de <strong style="color:#0A1628;">${childFirst}</strong> al Departamento de Salud Comunitaria de Georgia para el Programa Pediátrico de Georgia (GAPP). Todo lo que necesitábamos de usted está completo y incluido en el expediente — gracias.`)}
      ${facts([
        ['Niño/a', childFull],
        ['Fecha de envío', fmt(submittedOn, 'es-US')],
        ['Enviado a', 'Departamento de Salud Comunitaria de Georgia'],
        ['Decisión esperada', `alrededor del ${fmt(decisionBy, 'es-US')}`],
      ])}
      ${nextSteps('Qué sucede ahora', [
        'El Estado revisa el expediente. La decisión suele tardar unos <strong>30 días</strong>.',
        'Le llamaremos o escribiremos en cuanto tengamos respuesta, sea cual sea.',
        '<strong>No necesita hacer nada en este momento.</strong>',
        'Si el Estado pide algo más, nos comunicaremos con usted de inmediato.',
      ])}
      ${p(`Si algo cambia antes de entonces — una nueva dirección o número de teléfono, una hospitalización, un cambio en la cobertura de Medicaid de ${childFirst} — por favor avísenos, ya que puede afectar la revisión.`)}
      ${contactBox('¿Preguntas? Con gusto le ayudamos.', 'Llame al', 'Escriba a')}`,
  } : {
    subject: `${childFirst}'s GAPP application has been submitted`,
    title: `${childFirst}'s application has been submitted`,
    category: 'GAPP Application',
    footer: 'This message is for the family of the child named above. Reply to this email or call us with any questions.',
    body: `
      ${p(`Dear ${guardian || 'Parent or Guardian'},`)}
      ${p(`A Heart For Care has submitted <strong style="color:#0A1628;">${childFirst}'s</strong> application packet to the Georgia Department of Community Health for the Georgia Pediatric Program (GAPP). Everything we needed from you is complete and in the packet &mdash; thank you.`)}
      ${facts([
        ['Child', childFull],
        ['Submitted on', fmt(submittedOn, 'en-US')],
        ['Submitted to', 'Georgia Department of Community Health'],
        ['Decision expected by', `on or around ${fmt(decisionBy, 'en-US')}`],
      ])}
      ${nextSteps('What happens next', [
        'The State reviews the packet. A decision usually takes about <strong>30 days</strong>.',
        'We will call or email you as soon as we hear back, whatever the outcome.',
        '<strong>Nothing is needed from you right now.</strong>',
        'If the State asks for anything further, we will contact you straight away.',
      ])}
      ${p(`If anything changes before then &mdash; a new address or phone number, a hospital stay, a change in ${childFirst}'s Medicaid coverage &mdash; please let us know, as it can affect the review.`)}
      ${contactBox('Questions? We are glad to help.', 'Call', 'Email')}`,
  };

  // Renewal and reconsideration: when the child is receiving care, the first
  // thing the family reads after "submitted" is that nothing stops. A paused
  // child's renewal leaves that line out rather than promise care that isn't
  // running.
  const expiry = currentExpiry ? new Date(`${String(currentExpiry).slice(0, 10)}T12:00:00`) : null;
  const changeEs = p(`Si algo cambia antes de entonces — una nueva dirección o número de teléfono, una hospitalización, un cambio en la cobertura de Medicaid de ${childFirst} — por favor avísenos, ya que puede afectar la revisión.`);
  const changeEn = p(`If anything changes before then &mdash; a new address or phone number, a hospital stay, a change in ${childFirst}'s Medicaid coverage &mdash; please let us know, as it can affect the review.`);
  const footerEs = 'Este mensaje es para la familia del niño mencionado. Puede responder a este correo o llamarnos con cualquier pregunta.';
  const footerEn = 'This message is for the family of the child named above. Reply to this email or call us with any questions.';
  const continuing = (k) => {
    const renewal = k === 'renewal';
    return es ? {
      subject: renewal ? `La renovación de GAPP de ${childFirst} ha sido enviada` : `La solicitud de reconsideración de GAPP de ${childFirst} ha sido enviada`,
      title: renewal ? `La renovación de ${childFirst} ha sido enviada` : `La solicitud de reconsideración de ${childFirst} ha sido enviada`,
      category: renewal ? 'Renovación GAPP' : 'Reconsideración GAPP',
      footer: footerEs,
      body: `
      ${p(`Estimado/a ${guardian || 'padre, madre o tutor'}:`)}
      ${p(renewal
        ? `A Heart For Care ha enviado la <strong style="color:#0A1628;">renovación</strong> de GAPP de <strong style="color:#0A1628;">${childFirst}</strong> al Departamento de Salud Comunitaria de Georgia. Es la revisión anual que mantiene autorizada la atención de enfermería de ${childFirst}. Todo lo que necesitábamos de usted está completo — gracias.`
        : `Hemos enviado al Departamento de Salud Comunitaria de Georgia la <strong style="color:#0A1628;">solicitud de reconsideración</strong> de <strong style="color:#0A1628;">${childFirst}</strong>, junto con su carta y los documentos de apoyo del médico. Pide al DCH que revise ${inService ? `las horas aprobadas para ${childFirst}` : `su decisión sobre la solicitud de ${childFirst}`}. Gracias por su ayuda con la carta.`)}
      ${facts([
        ['Niño/a', childFull],
        [renewal ? 'Renovación enviada el' : 'Enviada el', fmt(submittedOn, 'es-US')],
        ...(expiry ? [['Autorización actual vigente hasta', fmt(expiry, 'es-US')]] : []),
        ['Decisión esperada', `alrededor del ${fmt(decisionBy, 'es-US')}`],
      ])}
      ${nextSteps('Qué sucede ahora', [
        ...(inService ? [renewal
          ? `<strong>La atención de ${childFirst} continúa con normalidad</strong>${expiry ? ' bajo la autorización actual' : ''}: no cambia nada con las enfermeras ni con el horario.`
          : `<strong>La atención de ${childFirst} continúa con las horas actuales</strong> mientras el DCH revisa la solicitud.`] : []),
        `El Estado revisa ${renewal ? 'la renovación' : 'la solicitud'}. La decisión suele tardar unos <strong>30 días</strong>.`,
        'Le llamaremos o escribiremos en cuanto tengamos respuesta, sea cual sea.',
        '<strong>No necesita hacer nada en este momento.</strong>',
        'Si el Estado pide algo más, nos comunicaremos con usted de inmediato.',
      ])}
      ${changeEs}
      ${contactBox('¿Preguntas? Con gusto le ayudamos.', 'Llame al', 'Escriba a')}`,
    } : {
      subject: renewal ? `${childFirst}'s GAPP renewal has been submitted` : `${childFirst}'s GAPP reconsideration request has been submitted`,
      title: renewal ? `${childFirst}'s renewal has been submitted` : `${childFirst}'s reconsideration request has been submitted`,
      category: renewal ? 'GAPP Renewal' : 'GAPP Reconsideration',
      footer: footerEn,
      body: `
      ${p(`Dear ${guardian || 'Parent or Guardian'},`)}
      ${p(renewal
        ? `A Heart For Care has submitted <strong style="color:#0A1628;">${childFirst}'s</strong> GAPP <strong style="color:#0A1628;">renewal</strong> to the Georgia Department of Community Health. This is the yearly review that keeps ${childFirst}'s nursing care authorized. Everything we needed from you is complete &mdash; thank you.`
        : `We have uploaded the <strong style="color:#0A1628;">reconsideration request</strong> for <strong style="color:#0A1628;">${childFirst}</strong> to the Georgia Department of Community Health, together with your letter and the doctor's supporting documents. It asks DCH to look again at ${inService ? `the hours approved for ${childFirst}` : `its decision on ${childFirst}'s application`}. Thank you for your help with the letter.`)}
      ${facts([
        ['Child', childFull],
        [renewal ? 'Renewal submitted on' : 'Submitted on', fmt(submittedOn, 'en-US')],
        ...(expiry ? [['Current authorization runs to', fmt(expiry, 'en-US')]] : []),
        ['Decision expected by', `on or around ${fmt(decisionBy, 'en-US')}`],
      ])}
      ${nextSteps('What happens next', [
        ...(inService ? [renewal
          ? `<strong>${childFirst}'s care continues as normal</strong>${expiry ? ' under the current authorization' : ''} &mdash; nothing changes with the nurses or the schedule.`
          : `<strong>${childFirst}'s care continues at the current hours</strong> while DCH reviews the request.`] : []),
        `The State reviews the ${renewal ? 'renewal' : 'request'}. A decision usually takes about <strong>30 days</strong>.`,
        'We will call or email you as soon as we hear back, whatever the outcome.',
        '<strong>Nothing is needed from you right now.</strong>',
        'If the State asks for anything further, we will contact you straight away.',
      ])}
      ${changeEn}
      ${contactBox('Questions? We are glad to help.', 'Call', 'Email')}`,
    };
  };

  const content = kind === 'application' ? application : continuing(kind);

  return sendDirect(
    to,
    content.subject,
    ahfcTemplate(content.title, content.body, null, null, '#0DA06A', content.category, content.footer),
    null,
    SUPPORT_EMAIL,
  );
};

// The application email, as the stage screen sends it on a hand move to
// Submitted (DMA80).
export const notifyDma80Submitted = (client, opts = {}) => notifyGappSubmitted(client, { ...opts, kind: 'application' });


// ── DCH decided a GAPP submission — sent to the family, cc the office ───────
// Reviewed before it goes: the office sees the address (prefilled from the
// record, editable for this send), whether to send at all, and the wording —
// a subject, an opening and a closing, prefilled per outcome and editable as
// plain text. The facts box, the reconsideration deadline and the signing
// button are built here from the decision itself, so an edit can never put a
// wrong number or date in front of the family.
//
// Outcomes: approved · reduced (approved, fewer hours than requested) · denied,
// and for a reconsideration: revised · upheld. Where DCH's Letter of
// Notification is to be signed, the link to sign it rides in this email — one
// email, not a decision email followed by a signing email.
const DECISION_WORD = {
  en: { renewal: 'renewal', initial: 'application', transfer: 'application', shared_hours: 'request', reconsideration: 'reconsideration request' },
  es: { renewal: 'renovación', initial: 'solicitud', transfer: 'solicitud', shared_hours: 'solicitud', reconsideration: 'solicitud de reconsideración' },
};
export const isSpanishFamily = (client) => /^\s*(spanish|espa)/i.test(client?.language || '');

// The wording the panel starts from. Plain text; blank lines start paragraphs.
export const gappDecisionWording = (client, { kind = 'renewal', outcome = 'approved', hasLetter = false, lang } = {}) => {
  const es = (lang || (isSpanishFamily(client) ? 'es' : 'en')) === 'es';
  const child = client?.first_name || (es ? 'su hijo/a' : 'your child');
  const word = DECISION_WORD[es ? 'es' : 'en'][kind] || DECISION_WORD[es ? 'es' : 'en'].renewal;
  const call = es ? `Llámenos al ${AHFC_TEL}.` : `Call us at ${AHFC_TEL}.`;
  if (es) {
    const signIt = 'Use el botón de abajo para leer la Carta de Notificación del DCH, marcar si está de acuerdo con las horas y firmarla. Pide su firma dos veces, como padre o madre y como cuidador/a principal.';
    const recon = 'Si no está de acuerdo, puede pedir al DCH que lo reconsidere; le ayudaremos a escribir la solicitud y la enviaremos por usted.';
    return {
      approved: { subject: `La ${word} de GAPP de ${child} ha sido aprobada`, opening: `Buenas noticias: el DCH ha aprobado la ${word} de GAPP de ${child}. Las horas aprobadas están abajo.`, closing: hasLetter ? `${signIt} ${call}` : `Pronto le enviaremos la Carta de Notificación del DCH para que la firme. ${call}` },
      reduced: { subject: `Decisión sobre la ${word} de GAPP de ${child}`, opening: `El DCH ha tomado su decisión sobre la ${word} de GAPP de ${child}. Las horas aprobadas son menos de las que solicitamos.`, closing: hasLetter ? `${signIt} ${recon} ${call}` : `${recon} ${call}` },
      denied: { subject: `Decisión sobre la ${word} de GAPP de ${child}`, opening: `Lamentamos informarle que el DCH no ha aprobado la ${word} de GAPP de ${child}.`, closing: `Tiene derecho a pedir al DCH que lo reconsidere. Le ayudaremos a escribir la solicitud y la enviaremos por usted. ${call} para empezar cuanto antes.` },
      revised: { subject: `El DCH ha revisado las horas de GAPP de ${child}`, opening: `El DCH ha respondido a la solicitud de reconsideración y ha revisado las horas de ${child}. Las nuevas horas están abajo.`, closing: hasLetter ? `${signIt} ${call}` : `Pronto le enviaremos la Carta de Notificación del DCH para que la firme. ${call}` },
      upheld: { subject: `Decisión sobre la reconsideración de GAPP de ${child}`, opening: 'El DCH ha respondido a la solicitud de reconsideración y ha mantenido su decisión original.', closing: `${call} Repasaremos con usted lo que esto significa para ${child} y qué se puede hacer a continuación.` },
    }[outcome];
  }
  const signIt = "Please use the button below to read DCH's Letter of Notification, mark whether you agree with the hours, and sign it. It asks for your signature twice, as parent and as primary caregiver.";
  const recon = 'If you disagree, you can ask DCH to reconsider, and we will help you write the request and upload it for you.';
  return {
    approved: { subject: `${child}'s GAPP ${word} has been approved`, opening: `Good news: DCH has approved ${child}'s GAPP ${word}. The approved hours are below.`, closing: hasLetter ? `${signIt} If you have any questions, call us at ${AHFC_TEL}.` : `We will send you DCH's Letter of Notification to sign shortly. If you have any questions, call us at ${AHFC_TEL}.` },
    reduced: { subject: `${child}'s GAPP ${word} decision`, opening: `DCH has made its decision on ${child}'s GAPP ${word}. The hours approved are lower than the hours we requested.`, closing: hasLetter ? `${signIt} ${recon} ${call}` : `${recon} ${call}` },
    denied: { subject: `${child}'s GAPP ${word} decision`, opening: `We are sorry to tell you that DCH has not approved ${child}'s GAPP ${word}.`, closing: `You have the right to ask DCH to reconsider. We will help you write the request and upload it for you. Please call us at ${AHFC_TEL} so we can start straight away.` },
    revised: { subject: `DCH has revised ${child}'s GAPP hours`, opening: `DCH has answered the reconsideration request and revised ${child}'s hours. The new hours are below.`, closing: hasLetter ? `${signIt} If you have any questions, call us at ${AHFC_TEL}.` : `We will send you DCH's Letter of Notification to sign shortly. If you have any questions, call us at ${AHFC_TEL}.` },
    upheld: { subject: `${child}'s GAPP reconsideration decision`, opening: 'DCH has answered the reconsideration request and kept its original decision.', closing: `Please call us at ${AHFC_TEL} and we will go through what this means for ${child} and what can be done next.` },
  }[outcome];
};

const escText = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Staff-written text: escaped, blank lines start paragraphs, single line
// breaks stay line breaks.
const paragraphs = (t) => String(t || '').trim().split(/\n\s*\n/).filter(Boolean)
  .map((para) => `<p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">${escText(para.trim()).replace(/\n/g, '<br />')}</p>`).join('');

// The email as it will be sent: { subject, html }. decision = { authNumber,
// skilled, pss, start, expiry, letterDate, reason } (ISO dates).
export const gappDecisionEmail = (client, { outcome = 'approved', wording, decision = {}, signUrl = null, lang } = {}) => {
  const es = (lang || (isSpanishFamily(client) ? 'es' : 'en')) === 'es';
  const locale = es ? 'es-US' : 'en-US';
  const day = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' }) : '');
  const guardian = [client?.guardian_first_name, client?.guardian_last_name].filter(Boolean).join(' ');
  const granted = ['approved', 'reduced', 'revised'].includes(outcome);
  const hrs = (n) => (es ? `${n} horas / semana` : `${n} hours / week`);
  const rows = granted ? [
    [es ? 'Número de PA' : 'PA number', escText(decision.authNumber)],
    [es ? 'Enfermería especializada' : 'Skilled nursing', decision.skilled != null && decision.skilled !== '' ? hrs(decision.skilled) : null],
    [es ? 'Apoyo personal (PSS)' : 'Personal support (PSS)', decision.pss != null && decision.pss !== '' ? hrs(decision.pss) : null],
    [es ? 'Autorizado' : 'Authorized', decision.start || decision.expiry ? `${day(decision.start)}${decision.expiry ? ` &ndash; ${day(decision.expiry)}` : ''}` : null],
  ] : [
    [es ? 'Decisión' : 'Decision', outcome === 'upheld' ? (es ? 'Decisión original mantenida' : 'Original decision kept') : (es ? 'No aprobada' : 'Not approved')],
    [es ? 'Motivo' : 'Reason', decision.reason ? escText(decision.reason) : null],
    [es ? 'Fecha de la carta' : 'Letter dated', day(decision.letterDate) || null],
  ];
  const facts = `
    <div style="background:#EAF0F8;border-left:4px solid #B9CBE0;border-radius:0 8px 8px 0;padding:16px 20px;margin:16px 0;">
      ${rows.filter(([, v]) => v).map(([label, value]) => `<div style="margin-bottom:6px;font-size:13px;"><span style="color:#0A1628;font-weight:500;display:inline-block;min-width:170px;">${label}</span><span style="color:#45526B;">${value}</span></div>`).join('')}
    </div>`;
  // A reduced or denied decision can be reconsidered — 30 calendar days from
  // the letter (GAPP §819.2), after which it is final.
  const due = ['reduced', 'denied'].includes(outcome) && decision.letterDate
    ? (() => { const d = new Date(`${String(decision.letterDate).slice(0, 10)}T12:00:00`); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })() : null;
  const deadline = due ? `
    <div style="background:#FFF8E6;border-left:4px solid #D4840A;border-radius:0 8px 8px 0;padding:14px 20px;margin:16px 0;font-size:13px;color:#45526B;line-height:1.6;">
      <strong style="color:#0A1628;">${es ? 'Fecha límite para pedir una reconsideración' : 'Deadline to ask for a reconsideration'}: ${day(due)}</strong><br />
      ${es ? '30 días desde la fecha de la carta del DCH. Después de esa fecha, la decisión es definitiva.' : "30 days from the date on DCH's letter. After that, the decision is final."}
    </div>` : '';
  const body = `
    <p style="color:#45526B;font-size:14px;line-height:1.7;margin:0 0 16px;">${es ? `Estimado/a ${escText(guardian) || 'padre, madre o tutor'}:` : `Dear ${escText(guardian) || 'Parent or Guardian'},`}</p>
    ${paragraphs(wording?.opening)}
    ${facts}
    ${deadline}
    ${paragraphs(wording?.closing)}`;
  const title = escText(wording?.subject || '');
  const category = es ? 'Decisión GAPP' : 'GAPP Decision';
  const footer = es ? 'Este mensaje es para la familia del niño mencionado. Puede responder a este correo o llamarnos con cualquier pregunta.' : 'This message is for the family of the child named above. Reply to this email or call us with any questions.';
  const html = ahfcTemplate(title, body, signUrl ? (es ? 'Revisar y firmar la carta' : 'Review & sign the letter') : null, signUrl, '#1A3A6B', category, footer);
  return { subject: String(wording?.subject || '').trim(), html };
};

export const sendGappDecision = (to, { subject, html }) => {
  const addr = String(to || '').trim();
  if (!addr) return Promise.resolve({ error: new Error('No email address') });
  return sendDirect(addr, subject, html, null, SUPPORT_EMAIL);
};
